import { Alert, Box, Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConnectedAccountsApi } from './types';
import type { FlowStep } from './useConnectedAccountsFlow';
import { useConnectedAccountsFlow } from './useConnectedAccountsFlow';

function parseLandingParams(): {
  status: string | null;
  proof: string | null;
  stepup: string | null;
  error: string | null;
} {
  // Hash-routed query: everything after the first `?` in the URL fragment.
  const hash = window.location.hash;
  const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const params = new URLSearchParams(qs);
  return {
    status: params.get('status'),
    proof: params.get('proof'),
    stepup: params.get('stepup'),
    error: params.get('error'),
  };
}

export interface ConnectedAccountsLandingProps {
  api: ConnectedAccountsApi;
  /** Where to send the user when they're done (the Connected Accounts page). */
  connectedAccountsHref: string;
  /** Called to navigate back to the Connected Accounts page. */
  onDone: () => void;
  /** Called when a delete flow completed here — the account is gone, so the
   * caller should log out and leave (not bounce back to the profile). */
  onDeleted?: () => void;
}

/**
 * Handles `#/account/linked?status=...` landings between the redirect legs of a
 * link/unlink flow. It resumes the state machine, shows a transient status, and
 * routes back to Connected Accounts.
 */
export function ConnectedAccountsLanding({
  api,
  connectedAccountsHref,
  onDone,
  onDeleted,
}: ConnectedAccountsLandingProps) {
  const { t } = useTranslation();
  const flow = useConnectedAccountsFlow(api);
  const [step, setStep] = useState<FlowStep | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = parseLandingParams();
    // Strip the single-use proof/step-up token from the URL + history right
    // after reading it, so it doesn't linger in the back/forward stack or get
    // copied from the address bar. The token rides the hash fragment (never sent
    // to a server), and the backend sets Referrer-Policy: no-referrer; this is
    // the remaining client-side leg.
    if (window.location.hash.includes('?')) {
      const cleanHash = window.location.hash.slice(0, window.location.hash.indexOf('?'));
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}${cleanHash}`
      );
    }
    flow
      .resumeFromLanding(params)
      .then((result) => {
        setStep(result);
        // Successful in-place completions bounce straight back to the list.
        if (result.kind === 'completed') {
          setTimeout(onDone, 1200);
        }
        if (result.kind === 'deleted') {
          setTimeout(() => onDeleted?.(), 1200);
        }
      })
      .catch((e: any) => {
        // Prefer the backend's structured error code (JSON `link/commit` etc.)
        // over the raw Axios message ("Request failed with status code 409"),
        // so the in-page banner can render the specific i18n string.
        setStep({ kind: 'error', code: e?.response?.data?.error || e?.message || 'unknown' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Center mih="50vh">
      <Box maw={420} w="100%">
        {step === null && (
          <Stack align="center" gap="md">
            <Loader />
            <Text c="dimmed">{t('connectedAccounts.processing', 'Finishing up…')}</Text>
          </Stack>
        )}

        {step?.kind === 'redirecting' && (
          <Stack align="center" gap="md">
            <Loader />
            <Text c="dimmed">{t('connectedAccounts.redirecting', 'Redirecting…')}</Text>
          </Stack>
        )}

        {step?.kind === 'nostr_sign_required' && (
          <Stack align="center" gap="md">
            <Title order={4}>{t('connectedAccounts.nostrSignTitle', 'Confirm with Nostr')}</Title>
            <Text c="dimmed" ta="center">
              {t(
                'connectedAccounts.nostrSignBody',
                "Sign a challenge with your Nostr extension to confirm it's you."
              )}
            </Text>
            <Button
              onClick={async () => {
                const pending = step;
                setStep(null);
                const result = await pending.sign();
                setStep(result);
                if (result.kind === 'completed') {
                  setTimeout(onDone, 1200);
                }
                if (result.kind === 'deleted') {
                  setTimeout(() => onDeleted?.(), 1200);
                }
              }}
            >
              {t('connectedAccounts.nostrSignButton', 'Sign with Nostr')}
            </Button>
          </Stack>
        )}

        {step?.kind === 'email_pending' && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="blue"
            title={t('connectedAccounts.checkEmailTitle', 'Check your email')}
          >
            {t(
              'connectedAccounts.checkEmail',
              'We sent a confirmation link to your email. Open it to finish.'
            )}
          </Alert>
        )}

        {step?.kind === 'completed' && (
          <Stack align="center" gap="md">
            <IconCircleCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>{t('connectedAccounts.linkedSuccess', 'Account linked')}</Title>
            <Button variant="light" component="a" href={connectedAccountsHref} onClick={onDone}>
              {t('connectedAccounts.backToProfile', 'Back to Profile')}
            </Button>
          </Stack>
        )}

        {step?.kind === 'deleted' && (
          <Stack align="center" gap="md">
            <IconCircleCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>{t('connectedAccounts.deletedSuccess', 'Account deleted')}</Title>
            <Text c="dimmed" ta="center">
              {t('connectedAccounts.deletedBody', 'Your account has been permanently deleted.')}
            </Text>
          </Stack>
        )}

        {step?.kind === 'error' && (
          <Stack gap="md">
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title={t('connectedAccounts.errorTitle', 'Something went wrong')}
            >
              {t(`connectedAccounts.error.${step.code}`, t('connectedAccounts.errorGeneric'))}
            </Alert>
            <Button variant="light" component="a" href={connectedAccountsHref} onClick={onDone}>
              {t('connectedAccounts.tryAgain', 'Try again')}
            </Button>
          </Stack>
        )}
      </Box>
    </Center>
  );
}
